import { QUOTE_STATUSES, type QuoteStatus } from '@/services/quotes'

/**
 * Etiqueta y color de un estado de cotización.
 *
 * Vive aquí y no en las vistas porque estaba copiado en dos archivos —el listado y
 * la ficha—, y dos copias divergen en cuanto una corrige un caso y la otra no. Con el
 * color por estado recién puesto hay casos que corregir, así que la copia se
 * elimina antes de que exista el segundo sitio donde mantenerla al día.
 *
 * Los seis estados están nombrados uno a uno, sin `default`. Un `default` que
 * devuelve gris es cómodo, y es justo lo que esconde el estado que se añadió
 * después sin que nadie se entere: se ve gris, parece un dato más, y nadie lo
 * revisa.
 */

/** Los colores de PrimeVue que usa un `Tag`. */
export type EstadoSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary'

/**
 * Etiqueta de un estado de cotización.
 *
 * Sale en mayúsculas porque el estado es la parte más mirada de la tabla y una clave
 * en minúsculas se lee como un dato crudo. Las mayúsculas van en la presentación y
 * no en el dato: lo que se envía al backend sigue siendo `enviada`.
 *
 * @param value La clave del estado. Se acepta cualquiera porque el tipo de la fila
 *   viene del servidor y no se puede narrowed antes de llegar aquí.
 */
export function statusLabel(value: string): string {
  const status = value as QuoteStatus

  return (ETIQUETAS[status] ?? value).toUpperCase()
}

/**
 * Color de un estado de cotización.
 *
 * El rojo se reserva para `cancelada`. Si todo fuera rojo, el comercial dejaría de
 * mirar el color y leería solo el texto, que es peor que no tener color.
 */
export function statusSeverity(value: string): EstadoSeverity {
  return COLORES[value as QuoteStatus] ?? 'secondary'
}

/**
 * Etiquetas en palabras, sin mayúsculas.
 *
 * Para los desplegables y para cualquier sitio donde la mayúscula estorbe —dentro de
 * una frase, o en una etiqueta que ya va en mayúscula por la posición que ocupa—. El
 * listado de estados de una opción tiene que leerse `Enviada`, no `ENVIADA`: ahí la
 * mayúscula es ruido y no dice nada.
 */
const ETIQUETAS: Record<QuoteStatus, string> = {
  borrador: 'Borrador',
  pendiente: 'Pendiente',
  separada: 'Separada',
  empacada: 'Empacada',
  enviada: 'Enviada',
  cancelada: 'Cancelada',
}

/** Colores, uno por estado. */
const COLORES: Record<QuoteStatus, EstadoSeverity> = {
  borrador: 'secondary',
  pendiente: 'warn',
  separada: 'info',
  empacada: 'info',
  enviada: 'success',
  cancelada: 'danger',
}

/**
 * Comprueba que la tabla de etiquetas cubre todos los estados.
 *
 * Existe porque el fallo es silencioso: un estado nuevo sin anotar en `ETIQUETAS` se
 * muestra tal cual llega, en minúsculas, y sin color, y no hay nada que lo delate.
 * Cuando se añada un estado a `QUOTE_STATUSES`, este recordatorio devuelve `false` y
 * el test falla pidiendo la etiqueta.
 */
export function estadosCubiertos(): boolean {
  return QUOTE_STATUSES.every((estado) => estado in ETIQUETAS && estado in COLORES)
}